import React, { useEffect, useRef, useState } from 'react';
import api from '../../services/api';

/**
 * EtatsFinanciersModuleFonctionnel - Système complet d'états financiers SYSCOHADA
 * Charge le fichier HTML complet fhc-etats-financiers.html dans une iframe
 * Avec communication bidirectionnelle pour sauvegarde/chargement depuis le backend
 * 
 * Fonctionnalités incluses :
 * - Identification de l'entité
 * - Bilan (Actif et Passif) avec calculs automatiques
 * - Compte de résultat
 * - Notes complémentaires
 * - Synthèse et résultats (ratios financiers, SIG, etc.)
 * - Sauvegarde en base de données
 * - Chargement des données sauvegardées
 */

const EtatsFinanciersModuleFonctionnel = () => {
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const htmlPath = `${process.env.PUBLIC_URL}/fichier_HTML/fhc-etats-financiers.html`;
  
  useEffect(() => {
    // Écouter les messages de l'iframe
    const handleMessage = async (event) => {
      // Sécurité : vérifier l'origine si nécessaire
      // if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'SAVE_ETATS_FINANCIERS') {
        await sauvegarderDonnees(event.data.data);
      }
      
      if (event.data.type === 'LOAD_REQUEST') {
        await chargerDernieresDonnees();
      }
      
      if (event.data.type === 'LOAD_BY_ID_REQUEST') {
        await chargerDonneesParId(event.data.id);
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  
  const sauvegarderDonnees = async (data) => {
    try {
      setLoading(true);
      
      // Déterminer si c'est une création ou une mise à jour
      if (data.id) {
        // Mise à jour
        await api.put(`/etats-financiers/${data.id}`, data);
        
        // Confirmer à l'iframe
        iframeRef.current?.contentWindow.postMessage({
          type: 'SAVE_SUCCESS',
          message: 'État financier mis à jour avec succès',
          id: data.id
        }, '*');
        
        alert('✅ État financier mis à jour avec succès !');
      } else {
        // Création
        const response = await api.post('/etats-financiers', data);
        
        // Confirmer à l'iframe avec l'ID créé
        iframeRef.current?.contentWindow.postMessage({
          type: 'SAVE_SUCCESS',
          message: 'État financier créé avec succès',
          id: response.data.etat_financier.id
        }, '*');
        
        alert('✅ État financier créé avec succès !');
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      
      iframeRef.current?.contentWindow.postMessage({
        type: 'SAVE_ERROR',
        message: 'Erreur lors de la sauvegarde: ' + (error.response?.data?.message || error.message)
      }, '*');
      
      alert('❌ Erreur lors de la sauvegarde. Vérifiez la console.');
    } finally {
      setLoading(false);
    }
  };
  
  const chargerDernieresDonnees = async () => {
    try {
      setLoading(true);
      const response = await api.get('/etats-financiers-latest');
      
      if (response.data.success) {
        // Envoyer les données à l'iframe
        iframeRef.current?.contentWindow.postMessage({
          type: 'LOAD_DATA',
          data: response.data.etat_financier
        }, '*');
      } else {
        alert('Aucun état financier trouvé');
      }
    } catch (error) {
      console.error('Erreur chargement:', error);
      alert('Aucun état financier sauvegardé trouvé');
    } finally {
      setLoading(false);
    }
  };
  
  const chargerDonneesParId = async (id) => {
    try {
      setLoading(true);
      const response = await api.get(`/etats-financiers/${id}`);
      
      if (response.data.success) {
        // Envoyer les données à l'iframe
        iframeRef.current?.contentWindow.postMessage({
          type: 'LOAD_DATA',
          data: response.data.etat_financier
        }, '*');
      }
    } catch (error) {
      console.error('Erreur chargement:', error);
      alert('État financier non trouvé');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div style={{ 
      width: '100%', 
      height: 'calc(100vh - 120px)',
      border: 'none',
      borderRadius: '8px',
      overflow: 'hidden',
      background: '#060d18',
      position: 'relative'
    }}>
      {loading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          color: '#C9A84C',
          fontSize: 14,
          fontWeight: 600
        }}>
          <div>⏳ Chargement...</div>
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={htmlPath}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          borderRadius: '8px'
        }}
        title="États Financiers SYSCOHADA - FHC Groupe"
        onError={(e) => {
          console.error('Erreur de chargement du fichier HTML:', htmlPath);
        }}
      />
    </div>
  );
};

export default EtatsFinanciersModuleFonctionnel;
